'use client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { ClawPassContent } from '@/app/components/shared/ClawPassContent';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { PlanDetail } from '@/app/components/shared/PlanDetail';
import { BonusCode } from '@/app/components/shared/PlanDetail/components/BonusCode';
import { PurchaseCreditsContent } from '@/app/components/shared/PurchaseCredits';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import { SubscriptionBanner } from '@/app/components/shared/SubscriptionBanner';
import { TeamPlanDetail } from '@/app/components/shared/TeamPlanDetail';
import { SITE } from '@/app/constants';
import { useFetchCreditProducts, useFetchProductList, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { PeriodType } from '@/app/types';
import { getTodayDateString, reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';
import { useTeamPurchaseAutoSwitch } from '../_hooks/use-team-purchase-auto-switch';
import type { EditionType } from '../types';
import { FAQ } from './FAQ';
import { Header } from './Header';
import { Help } from './Help';
import { HelpFloatButton } from './HelpFloatButton';
import { OrderListDialog } from './OrderList';
import { PaymentTabs } from './PaymentTabs';

export type ActiveTabType = 'plan' | 'credits' | 'clawPass';
export type { EditionType };

// 订阅页切到「创作者计划」tab 时，同一自然日只弹一次「购买协议变更」提示弹窗
const CLAW_AGREEMENT_CHANGE_SUBSCRIPTION_LAST_SHOWN_DATE_KEY =
  'claw_agreement_change_dialog_subscription_last_shown_date';

type PaymentProps = {
  edition: EditionType;
};

export const Payment: FC<PaymentProps> = ({ edition: editionFromRoute }) => {
  const { isEligible: isRetentionEligible } = useRetentionStrategy();
  const showBonusCode = SITE === 'china';
  const {
    userInfo,
    isUserInfoInitialized,
    isSubscriptionInitialized,
    isClawInfoInitialized,
    subscription,
    isTeamSpace,
    teamSpaceInfo,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    isSubscriptionInitialized: state.isSubscriptionInitialized,
    isClawInfoInitialized: state.isClawInfoInitialized,
    subscription: state.subscription,
    isTeamSpace: state.isTeamSpace,
    teamSpaceInfo: state.teamSpaceInfo,
  }));
  const searchParams = useSearchParams();
  const [urlBonusCode, setUrlBonusCode] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<ActiveTabType>((searchParams.get('tab') as ActiveTabType) || 'plan');
  const edition: EditionType = editionFromRoute;
  const [subscriptionPeriod, setSubscriptionPeriod] = useState<PeriodType | null>(null);
  const router = useRouter();
  const t = useTranslations();
  const { open: openClawAgreementChangeDialog } = useGlobalModal('clawAgreementChangeDialog');
  const isFree = userInfo?.subs_plan === 'free';
  const isPersonalEdition = edition === 'personal';
  const canAccessCreditsTab =
    isUserInfoInitialized && Boolean(userInfo) && isPersonalEdition && !isFree && !isTeamSpace;

  // 企业版支付成功 + 弹窗关闭后，自动切换到新创建的团队空间
  useTeamPurchaseAutoSwitch();

  const [openOrderList, setOpenOrderList] = useState(false);

  const {
    getProductsList,
    loading: rawLoadingProducts,
    error,
    clawPassProducts,
    subscriptionProducts,
    subscriptionTabList,
    subscriptionInitialPeriod,
    subscriptionPaidTrialInfo,
    teamSubscriptionProducts,
    teamSubscriptionTabList,
    teamSubscriptionInitialPeriod,
  } = useFetchProductList();
  const {
    creditsProducts,
    loading: rawLoadingCreditProducts,
    error: creditProductsError,
  } = useFetchCreditProducts({
    enabled: canAccessCreditsTab && activeTab === 'credits' && isSubscriptionInitialized,
  });

  // 前置依赖未就绪也算 loading，避免页面先闪一下空状态再切到 loader
  const loadingProducts = rawLoadingProducts || !isSubscriptionInitialized || !isClawInfoInitialized;
  const loadingCreditProducts = rawLoadingCreditProducts || !isSubscriptionInitialized;

  useEffect(() => {
    if (!isUserInfoInitialized) return;

    // 团队空间用户访问 personal 时，优先跳到 team，保留所有 query
    if (isTeamSpace && edition === 'personal') {
      const query = searchParams.toString();
      router.replace(query ? `/subscription/team?${query}` : '/subscription/team');
      return;
    }

    const currentQuery = searchParams.toString();
    const newSearchParams = new URLSearchParams(currentQuery);
    if (!userInfo) {
      const basePath = `/subscription/${edition}`;
      const redirectPath = currentQuery ? `${basePath}?${currentQuery}` : basePath;
      router.replace(`/login?redirect=${encodeURIComponent(redirectPath)}`);
    } else {
      let shouldNormalizeUrl = false;

      const tab = searchParams.get('tab') as ActiveTabType;
      if (tab && (tab === 'plan' || tab === 'credits')) {
        setActiveTab(tab);
        newSearchParams.delete('tab');
        shouldNormalizeUrl = true;
      }
      const subscriptionPeriod = searchParams.get('subscription-period') as PeriodType | null;
      if (subscriptionPeriod) {
        setSubscriptionPeriod(subscriptionPeriod);
        newSearchParams.delete('subscription-period');
        shouldNormalizeUrl = true;
      }
      const bonus_code = searchParams.get('bonus-code') as string | undefined;
      if (bonus_code) {
        setUrlBonusCode(bonus_code);
        newSearchParams.delete('bonus-code');
        shouldNormalizeUrl = true;
      }

      if (shouldNormalizeUrl) {
        const nextQuery = newSearchParams.toString();
        if (nextQuery !== currentQuery) {
          const basePath = `/subscription/${edition}`;
          router.replace(nextQuery ? `${basePath}?${nextQuery}` : basePath);
        }
      }
    }
  }, [isUserInfoInitialized, isTeamSpace, userInfo, searchParams, edition]);

  useEffect(() => {
    if (isSubscriptionInitialized && isClawInfoInitialized) {
      getProductsList();
    }
  }, [isSubscriptionInitialized, isClawInfoInitialized]);

  // 企业版下积分包/创作者计划不可见；免费用户和团队空间也不能通过 URL 强行进入积分 tab。
  useEffect(() => {
    if ((edition === 'team' && activeTab !== 'plan') || (activeTab === 'credits' && !canAccessCreditsTab)) {
      setActiveTab('plan');
    }
  }, [edition, activeTab, canAccessCreditsTab]);

  // 切到「创作者计划」tab 时盖一次「购买协议变更」提示，同一自然日仅一次
  useEffect(() => {
    if (edition !== 'team' && activeTab === 'clawPass') {
      const today = getTodayDateString();
      if (safeStorage.getItem(CLAW_AGREEMENT_CHANGE_SUBSCRIPTION_LAST_SHOWN_DATE_KEY) !== today) {
        safeStorage.setItem(CLAW_AGREEMENT_CHANGE_SUBSCRIPTION_LAST_SHOWN_DATE_KEY, today);
        openClawAgreementChangeDialog({ fromPage: 'subscription_tab' });
      }
    }
  }, [edition, activeTab, openClawAgreementChangeDialog]);

  const handleEditionChange = (next: EditionType) => {
    if (next === edition) return;
    // 切换 edition 必须显式重置 tab：page 走 'use cache'，client-side router.replace 不会 remount Payment，
    // 上一档残留的 credits/clawPass 选中态会带过来
    setActiveTab('plan');
    const params = new URLSearchParams(searchParams.toString());
    params.delete('edition');
    const nextQuery = params.toString();
    const basePath = `/subscription/${next}`;
    router.replace(nextQuery ? `${basePath}?${nextQuery}` : basePath);
    reportEvent('element_click', {
      from_page: 'my-subscription',
      element_name: next === 'team' ? 'team_edition_tab' : 'personal_edition_tab',
    });
  };

  if (!isUserInfoInitialized) {
    return <GlobalLoader className="size-10" />;
  }

  // 团队空间下被锁定为 team edition，不显示切换器
  const showEditionSwitcher = !isTeamSpace;
  // 企业成员（member）只能查看不能购买；管理员/owner/个人空间用户都可购买
  const isTeamMember = isTeamSpace && teamSpaceInfo?.role === 'member';
  const isTeamPurchaseDisabled = edition === 'team' && isTeamMember;

  const editionOptions: { key: EditionType; label: string }[] = [
    { key: 'personal', label: t('payments.mySubscription.personalEdition') },
    { key: 'team', label: t('payments.mySubscription.teamEdition') },
  ];

  const tabs: { key: ActiveTabType; label: string; onClick: () => void }[] = [
    {
      key: 'plan',
      label: t('payments.mySubscription.plans'),
      onClick: () => {
        setActiveTab('plan');
        reportEvent('element_click', { from_page: 'my-subscription', element_name: 'plan_tab' });
      },
    },
    // 企业版只展示套餐 tab；个人版下团队空间用户和免费用户不能购买积分
    ...(isPersonalEdition && !isFree && !isTeamSpace
      ? [
          {
            key: 'credits' as const,
            label: t('payments.mySubscription.credits'),
            onClick: () => {
              setActiveTab('credits');
              reportEvent('element_click', { from_page: 'my-subscription', element_name: 'purchase_credits_tab' });
              if (isRetentionEligible) {
                reportEvent('subscription_reactivation_cancel_click', { pricing: 'credit' });
              }
            },
          },
        ]
      : []),
    ...(isPersonalEdition && clawPassProducts.length > 0
      ? [
          {
            key: 'clawPass' as const,
            label: t('payments.mySubscription.clawPass'),
            onClick: () => {
              setActiveTab('clawPass');
              reportEvent('element_click', { from_page: 'my-subscription', element_name: 'claw_pass_tab' });
            },
          },
        ]
      : []),
  ];

  return (
    <div className="flex w-full max-w-[1336px] select-none flex-col justify-center sm:max-w-full sm:overflow-auto sm:pb-12">
      <Header setOpenOrderList={setOpenOrderList} />

      <PaymentTabs
        tabs={tabs}
        activeTab={activeTab}
        trailingContent={
          showBonusCode ? (
            <BonusCode
              bonusCodeProps={urlBonusCode}
              setBonusCodeProps={setUrlBonusCode}
              className="rounded-12 px-4 py-2"
            />
          ) : null
        }
      />

      {activeTab === 'plan' && (
        <>
          {isPersonalEdition && <SubscriptionBanner paidTrialInfo={subscriptionPaidTrialInfo} />}
          {subscription?.pay_channel === 'apple' ? (
            <div className="mt-2 rounded-4 border border-system-white12 bg-[#1B2A3C] px-3 py-2 text-system-white text-xs">
              {t('payments.mySubscription.applePayDesc')}
            </div>
          ) : subscription?.pay_channel === 'google' ? (
            <div className="mt-2 rounded-4 border border-system-white12 bg-[#1B2A3C] px-3 py-2 text-system-white text-xs">
              {t('payments.mySubscription.googlePayDesc')}
            </div>
          ) : null}
          {isPersonalEdition ? (
            <PlanDetail
              loadingProducts={loadingProducts}
              products={subscriptionProducts}
              tabList={subscriptionTabList}
              error={error}
              period={subscriptionPeriod || subscriptionInitialPeriod}
              paidTrialInfo={subscriptionPaidTrialInfo}
              editionOptions={showEditionSwitcher ? editionOptions : undefined}
              edition={edition}
              onEditionChange={handleEditionChange}
            />
          ) : (
            <TeamPlanDetail
              loadingProducts={loadingProducts}
              products={teamSubscriptionProducts}
              tabList={teamSubscriptionTabList}
              initialPeriod={teamSubscriptionInitialPeriod}
              error={error}
              purchaseDisabled={isTeamPurchaseDisabled}
              editionOptions={showEditionSwitcher ? editionOptions : undefined}
              edition={edition}
              onEditionChange={handleEditionChange}
            />
          )}
        </>
      )}
      {activeTab === 'credits' && (
        <PurchaseCreditsContent
          source={'my-subscription'}
          loadingProducts={loadingCreditProducts}
          error={creditProductsError}
          products={creditsProducts}
        />
      )}
      {activeTab === 'clawPass' && <ClawPassContent products={clawPassProducts} loadingProducts={loadingProducts} />}

      <FAQ />
      <Help />
      <OrderListDialog open={openOrderList} onOpenChange={setOpenOrderList} />
      <HelpFloatButton />
    </div>
  );
};
